import { PropsWithChildren, useMemo } from 'react';

import { CellContext, Header, HeaderGroup, flexRender } from '@tanstack/react-table';
import { Card, Group, MantineTable, Stack } from 'ui';

import { TableBody } from '../TableContent/TableBody/TableBody';
import { TableDataProviderComponent } from '../TableDataProvider';
import { TableRoot } from '../TableRoot/TableRoot';
import { useTableContext } from '../table.context';
import { TableColumns } from '../types/tableColumns.types';

type PrinttyTableProps<TData> = {
  data: TData[];
  columns: TableColumns<unknown>;
  tableHeader?: React.ReactNode;
  children?: React.ReactNode;
};

interface TableHeaderProps<TData>
  extends Pick<PrinttyTableProps<TData>, 'tableHeader' | 'columns'> {}

interface PrinttyPrintTableInfoBoxProps {
  leftItems: { label: string; detail: string }[];
}

const PdfTableHeader = <TData,>({ tableHeader, columns }: TableHeaderProps<TData>) => {
  const { table } = useTableContext();
  const tableColSpan = useMemo(() => columns.length, [columns.length]);

  const isPdf = table.options.meta?.isPdf;
  return (
    <MantineTable.Thead>
      <MantineTable.Tr key="header-element" bg="white">
        <MantineTable.Th bg="white" colSpan={tableColSpan}>
          {tableHeader}
        </MantineTable.Th>
      </MantineTable.Tr>
      {table.getHeaderGroups().map((headerGroup: HeaderGroup<unknown>) => (
        <MantineTable.Tr key={headerGroup.id}>
          {headerGroup.headers.map((header: Header<unknown, unknown>) => {
            // isPrintable doesn't contain a default, so we need to explicitly check for false
            if (isPdf && header.column?.columnDef?.meta?.isPrintable === false) return null;
            return (
              <MantineTable.Th
                colSpan={header.colSpan}
                style={{
                  width: header.getSize(),
                }}
                key={header.id}
              >
                <Group wrap="nowrap">
                  {header.isPlaceholder ? null : (
                    <div style={{ whiteSpace: 'nowrap' }}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                  )}
                </Group>
              </MantineTable.Th>
            );
          })}
        </MantineTable.Tr>
      ))}
    </MantineTable.Thead>
  );
};

const PdfTableBody = ({ tableRowClassId }: { tableRowClassId: string }) => {
  return <TableBody tableRowClassId={tableRowClassId} />;
};

const PdfTable = <TData,>({ children, data, columns }: PrinttyTableProps<TData>) => {
  return (
    <TableRoot tableMeta={{ isPdf: true }}>
      <Stack>
        <Card
          radius="sm"
          withBorder
          styles={(theme) => ({ root: { border: `1px solid ${theme.colors.gray[1]}` } })}
          bg="white"
        >
          <MantineTable>{children}</MantineTable>
        </Card>
      </Stack>
      <TableDataProviderComponent data={data} columns={columns} />
    </TableRoot>
  );
};

/**
 * Wraps a component into a conditional wrapper for components that do not return
 * pure values for pdf rendering
 */
const PdfCell = (
  props: CellContext<any, unknown> & PropsWithChildren<{ pdfRender: number | string | boolean }>
) => {
  if (props.table.options?.meta?.isPdf) {
    return <>{props.pdfRender}</>;
  }

  return props.children;
};

export { PdfCell, PdfTable, PdfTableBody, PdfTableHeader };

export type { PrinttyPrintTableInfoBoxProps, PrinttyTableProps };
